<script setup lang="ts">
import { Analytics } from '@vercel/analytics/nuxt'
import { useSnackbarStore } from '~/stores/snackbar'
import { APP_LOCALES, DEFAULT_LOCALE, defaultLocalePath, hasOwnContent, type AppLocale } from '~/constants/appLocale'
import { isAffiliateEmbedPath } from '~/constants/affiliate'

const OG_LOCALES: Record<AppLocale, string> = { en: 'en_US', cs: 'cs_CZ', de: 'de_DE', pl: 'pl_PL' }

const route = useRoute()
const { canonicalFor } = useSiteUrl()
const { locale } = useI18n()
const localize = useLocalizedPath()
// A partner's widget, inside someone else's page (pages/embed/[code].vue).
const embedded = computed(() => isAffiliateEmbedPath(route.path))

// Each translation of a marketing page names the others, so search engines
// show the visitor's language (constants/appLocale.ts). Pages with a single
// URL (the app, the blog) have none, and a language without the page's own
// text (constants/appLocale.ts, hasOwnContent) is left out.
const englishPath = computed(() => defaultLocalePath(route.path))
const alternates = computed(() => {
  // The Czech and German blogs have one language each: their posts have no translations (constants/blog.ts).
  if (!isLocalizedRoute(route) || route.meta.blogLocale) return []
  const href = (code: typeof APP_LOCALES[number]) => canonicalFor(localize(route.path, code))
  return [
    ...APP_LOCALES.filter(code => hasOwnContent(englishPath.value, code)).map(code => ({ rel: 'alternate', hreflang: code, href: href(code), key: `hreflang-${code}` })),
    { rel: 'alternate', hreflang: 'x-default', href: href(DEFAULT_LOCALE), key: 'hreflang-x-default' },
  ]
})

useHead(() => ({
  // Screen readers and search engines read the page language from here: the
  // URL's on the marketing site, the visitor's in the app
  // (middleware/locale.global.ts).
  htmlAttrs: { lang: locale.value },
  // Brand suffix only where it fits in ~60 characters, and never twice
  // (utils/seoTitle.ts).
  titleTemplate: (titleChunk?: string) => brandedTitle(titleChunk),
  link: [
    { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico?v=2' },
    { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png?v=2' },
    { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png?v=2' },
    { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png?v=2' },
    { rel: 'manifest', href: '/site.webmanifest?v=2' },
    // Site-wide self-referencing canonical. `key` lets a page REPLACE this tag
    // rather than emit a second one (unhead dedupes links by key) — the
    // paginated blog routes rely on that. Query strings are dropped on purpose.
    { rel: 'canonical', key: 'canonical', href: canonicalFor(route.path) },
    ...alternates.value,
  ],
  meta: [
    // No maximum-scale / user-scalable=no: blocking pinch-zoom is a WCAG 1.4.4 failure.
    { name: 'viewport', content: 'width=device-width, initial-scale=1, viewport-fit=cover' },
    { name: 'theme-color', content: '#ffffff' },
    { property: 'og:locale', content: OG_LOCALES[locale.value as AppLocale] ?? 'en_US' },
    // An English text under a translated URL is not that language's page.
    ...(isLocalizedRoute(route) && !hasOwnContent(englishPath.value, locale.value as AppLocale)
      ? [{ name: 'robots', content: 'noindex, follow', key: 'robots' }]
      : []),
  ],
}))

const snackbarStore = useSnackbarStore()

// Premium modals management (the presell offer itself is retired, see usePremiumModals)
const { onCheckoutModalClose } = usePremiumModals()

// Listen for checkout modal close events globally
if (import.meta.client) {
  onMounted(() => {
    // Listen for custom event from checkout modals
    window.addEventListener('checkout-modal-closed', (event: Event) => {
      type CheckoutSource = 'cv-complete' | 'ai-limit' | 'default'
      const source = (event as CustomEvent<{ source?: CheckoutSource }>).detail?.source || 'default'
      onCheckoutModalClose(source)
    })
  })
}

</script>

<template>
  <div class="flex flex-1 flex-col">
    <NuxtLayout>
      <NuxtPage :style="{ flexGrow: 1 }" />
    </NuxtLayout>
    <CommonSnackbar
      :show="snackbarStore.show"
      :message="snackbarStore.message"
      :type="snackbarStore.type"
    />
    <!-- Not inside the web-CV preview iframe: the parent page already asks.
         Nor in a partner's widget: it sets no cookies and records no visit. -->
    <CommonCookieBanner v-if="route.path !== '/web-preview' && !embedded" />
    <Analytics v-if="!embedded" />

    <!-- Global Export Credit Confirmation Dialog -->
    <ClientOnly>
      <CVExportCreditConfirmDialog />
    </ClientOnly>
  </div>
</template>

<style>
#__nuxt {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}
</style>
